import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { PDFDocument } from "pdf-lib";
import {
  fetchFollowUpLetter,
  getFulFileBytes,
  getFulFiles,
  updateFollowUpLetter,
} from "../../api/reportsApi";
import { FollowUpLetterFiles, FUL_FILES_QUERY_KEY } from "./FollowUpLetterFiles";
import { FollowUpLetterDto } from "../../types/Rows";
import { Button } from "../../components/ui/button";
import { NavigationBadge, useNavigationBadgesStore } from "../..";
import { pdf } from "@react-pdf/renderer";
import { useUserContext } from "../user/authProvider";
import { FollowUpLetterDocGenerator, FUL_SECTIONS } from "./followUpLetterDocGenerator";
import { ItemPopover } from "../../components/atomic/itemPopover";
import { Modal, ModalBody, ModalHeader } from "../../components/ui/Modal";
import { field } from "../../components/ui/styles";
import { ReportDataPanel } from "./ReportDataPanel";
import { GeneratedDocumentsList, useStoreGeneratedDocument } from "../../components/generatedDocuments/GeneratedDocuments";
import { PdfDesignPicker, toVariant, usePdfDesign } from "../../components/pdfDesign/PdfDesign";
import { FollowUpLetterDocumentV2 } from "../report/document/v2/LetterDocumentsV2";
import { FormFieldsSkeleton } from "../../components/ui/Skeleton";

function FollowUpLetterContent({
  letterId,
  isModal,
}: {
  letterId: string | undefined;
  isModal?: boolean;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { parsedName } = useUserContext();
  const [followUpLetter, setFollowUpLetter] =
    useState<FollowUpLetterDto | null>(null);

  const { data } = useQuery(
    ["FollowUpLetter", letterId],
    async () => fetchFollowUpLetter(letterId),
    {
      enabled: letterId != null,
      initialData: null,
    }
  );

  const { data: fulFiles = [] } = useQuery(
    FUL_FILES_QUERY_KEY(letterId),
    async () => getFulFiles(letterId),
    { enabled: letterId != null }
  );

  const [footerText, setFooterText] = useState("MONITORING VISIT FOLLOW-UP LETTER");
  const [versionText, setVersionText] = useState("Wersja 1.0 PL, 19 LISTOPAD 2022");
  const [showExportModal, setShowExportModal] = useState(false);
  const [enabledSections, setEnabledSections] = useState<Set<string>>(
    () => new Set(FUL_SECTIONS.map((s) => s.key))
  );

  const setItems = useNavigationBadgesStore((state: any) => state.setItems);
  const storeCopy = useStoreGeneratedDocument(letterId);

  useEffect(() => {
    if (data) {
      if (!isModal) {
        setItems([
          { text: "Badania", url: "/" } as NavigationBadge,
          { text: data.studyName, url: "/study/" + data.studyId } as NavigationBadge,
          { text: data.reportName, url: "/reports/" + data.reportId } as NavigationBadge,
          { text: "Follow-up letter", url: "" } as NavigationBadge,
        ]);
      }
    }
  }, [data, setItems, isModal]);

  const { mutate: updateLetter } = useMutation(
    async (letter: FollowUpLetterDto) => updateFollowUpLetter(letterId, letter),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["FollowUpLetter", letterId]);
      },
    }
  );

  useEffect(() => {
    if (data) setFollowUpLetter(data);
  }, [data]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFollowUpLetter((prev) => ({ ...prev, [name]: value }));
  };

  const [pdfDesign, choosePdfDesign] = usePdfDesign();
  const handleDownload = async (sections: Set<string>) => {
    const attachmentsToMerge = fulFiles.filter((f) => f.includeInPdf);
    const docProps = {
      followUpLetter,
      author: parsedName,
      footerText,
      versionText,
      sections,
      attachments: attachmentsToMerge,
      participants: followUpLetter.participants ?? [],
    };
    const variant = toVariant(pdfDesign);

    const mainBlob = await pdf(
      variant ? <FollowUpLetterDocumentV2 variant={variant} {...docProps} /> : <FollowUpLetterDocGenerator {...docProps} />
    ).toBlob();

    let finalBytes: Uint8Array;

    if (attachmentsToMerge.length > 0) {
      const merged = await PDFDocument.load(await mainBlob.arrayBuffer());
      for (const att of attachmentsToMerge) {
        try {
          const attBytes = await getFulFileBytes(letterId, att.id);
          const ext = att.name.split(".").pop()?.toLowerCase();
          if (ext === "jpg" || ext === "jpeg" || ext === "png") {
            const imgPdf = await PDFDocument.create();
            const img = ext === "png"
              ? await imgPdf.embedPng(attBytes)
              : await imgPdf.embedJpg(attBytes);
            const A4 = { width: 595, height: 842 };
            const scale = Math.min(A4.width / img.width, A4.height / img.height);
            const w = img.width * scale;
            const h = img.height * scale;
            const page = imgPdf.addPage([A4.width, A4.height]);
            page.drawImage(img, { x: (A4.width - w) / 2, y: (A4.height - h) / 2, width: w, height: h });
            const pages = await merged.copyPages(imgPdf, imgPdf.getPageIndices());
            pages.forEach((p) => merged.addPage(p));
          } else {
            const attPdf = await PDFDocument.load(attBytes);
            const pages = await merged.copyPages(attPdf, attPdf.getPageIndices());
            pages.forEach((p) => merged.addPage(p));
          }
        } catch {
          // skip attachments that fail to load (non-PDF or unavailable)
        }
      }
      finalBytes = await merged.save();
    } else {
      finalBytes = new Uint8Array(await mainBlob.arrayBuffer());
    }

    const fileName = `Follow-up-letter-${data.reportName}-${data.resortName}-${data.studyName}.pdf`;
    const finalBlob = new Blob([finalBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(finalBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportModal(false);
    storeCopy("FUL", finalBlob, fileName); // SR-035: kopia w historii
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (followUpLetter) updateLetter(followUpLetter);
  };

  const handleApplyFromReport = (patch: Partial<FollowUpLetterDto>) => {
    setFollowUpLetter((prev) => ({ ...prev, ...patch }));
  };

  const toggleSection = (key: string) => {
    setEnabledSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!followUpLetter)
    return isModal ? (
      <FormFieldsSkeleton />
    ) : (
      <div className="p-6 bg-[#F7F7F5] rounded-[12px] max-w-lg mx-auto m-5">
        <FormFieldsSkeleton />
      </div>
    );

  const inputCls = "w-full rounded-lg border border-[#EBEBEA] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[#F4A832]";
  const labelCls = "block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1";

  const form = (
    <>
      <Modal open={showExportModal} onClose={() => setShowExportModal(false)} size="sm" offset="sm">
        <ModalHeader title="Wybierz sekcje do eksportu" onClose={() => setShowExportModal(false)} />
        <ModalBody>
          <div className="mb-2 pb-2 border-b border-[var(--sc-border)]">
            <PdfDesignPicker value={pdfDesign} onChange={choosePdfDesign} name="pdf-design-ful" />
          </div>
          <div className="flex flex-col gap-1">
            {FUL_SECTIONS.map((s) => (
              <label key={s.key} className="flex items-center gap-2.5 px-2 py-1.5 rounded-[6px] hover:bg-[var(--sc-bg)] cursor-pointer text-sm text-[var(--sc-text)]">
                <input
                  type="checkbox"
                  className={field.checkbox}
                  checked={enabledSections.has(s.key)}
                  onChange={() => toggleSection(s.key)}
                />
                {s.label}
              </label>
            ))}
          </div>
          <div className="pt-3 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowExportModal(false)}>Anuluj</Button>
            <Button size="sm" onClick={() => handleDownload(enabledSections)}>Pobierz PDF</Button>
          </div>
        </ModalBody>
      </Modal>

      <form onSubmit={handleSubmit} className="space-y-4">
        <GeneratedDocumentsList reportId={letterId} type="FUL" />
        <ReportDataPanel data={followUpLetter} onApply={handleApplyFromReport} />

        <div>
          <label className={labelCls}>Numer protokołu:</label>
          <input type="text" name="protocolNumber" value={followUpLetter.protocolNumber ?? ""} onChange={handleChange} className={inputCls} />
          <label className={`${labelCls} mt-3`}>Akronim badania:</label>
          <input type="text" name="studyAcronym" value={followUpLetter.studyAcronym ?? ""} onChange={handleChange} className={inputCls} />
          <label className={`${labelCls} mt-3`}>{t("sponsor")}:</label>
          <input type="text" name="sponsor" value={followUpLetter.sponsor} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>
            {t("introductionText")}:
            <ItemPopover text="Szanowny Panie/Pani + tytuł naukowy, np. Szanowna Pani Profesor/Szanowny Panie Docencie" />
          </label>
          <textarea name="introductionText" value={followUpLetter.introductionText} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("visitType")}:</label>
          <input type="text" name="visitType" value={followUpLetter.visitType} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("croParticipants")}:</label>
          <textarea name="croParticipants" value={followUpLetter.croParticipants} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("resortParticipants")}:</label>
          <textarea name="resortParticipants" value={followUpLetter.resortParticipants} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("studyProgress")}:</label>
          <textarea name="studyProgress" value={followUpLetter.studyProgress} onChange={handleChange} className={inputCls} rows={5} />
        </div>
        <div>
          <label className={labelCls}>{t("followUpLetterNumber")}:</label>
          <input type="number" name="followUpLetterNumber" value={followUpLetter.followUpLetterNumber} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("amountForSiv")}:</label>
          <input
            type="number"
            name="declaredForSiv"
            value={followUpLetter.declaredForSiv}
            onChange={(e) => setFollowUpLetter((prev) => ({ ...prev, declaredForSiv: Number(e.target.value) }))}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Status Ośrodka:</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {([
              ["declaredParticipants", "Zadeklarowani uczestnicy"],
              ["amountForScreening",   "W screeningu"],
              ["amountOfRandomized",   "Zrandomizowani"],
              ["screenFailure",        "Niepowodzenia screeningu"],
              ["duringTreatment",      "W trakcie leczenia"],
              ["protocolDeviation",    "Odchylenia od protokołu"],
              ["reportedSAE",          "Zdarzenia SAE"],
            ] as [string, string][]).map(([field, label]) => (
              <div key={field}>
                <label className={labelCls}>{label}:</label>
                <input
                  type="number"
                  value={(followUpLetter as any)[field] ?? 0}
                  onChange={(e) => setFollowUpLetter((prev) => ({ ...prev, [field]: Number(e.target.value) }))}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>Podsumowanie (komentarz końcowy z raportu):</label>
          <textarea
            name="finalComment"
            value={followUpLetter.finalComment ?? ""}
            onChange={handleChange}
            className={inputCls}
            rows={4}
          />
        </div>

        {followUpLetter.participants?.length > 0 && (
          <div>
            <label className={labelCls}>Zakres monitorowanych danych (z raportu):</label>
            <div className="overflow-x-auto rounded-lg border border-[#EBEBEA] mt-1">
              <table className="w-full text-xs">
                <thead className="bg-[#F7F7F5] text-[var(--sc-muted)]">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Nr uczestnika</th>
                    <th className="text-left px-3 py-2 font-medium">Nr wizyty</th>
                    <th className="text-left px-3 py-2 font-medium">100% SDV</th>
                    <th className="text-left px-3 py-2 font-medium">RBV</th>
                    <th className="text-left px-3 py-2 font-medium">Komentarz</th>
                  </tr>
                </thead>
                <tbody>
                  {followUpLetter.participants.map((p, i) => (
                    <tr key={i} className="border-t border-[#EBEBEA]">
                      <td className="px-3 py-1.5 text-[var(--sc-text)]">{i + 1}. {p.participantNumber}</td>
                      <td className="px-3 py-1.5 text-[var(--sc-text)]">{p.visitNumber}</td>
                      <td className="px-3 py-1.5 text-[var(--sc-text)]">{p.isFinshed ? "TAK" : "NIE"}</td>
                      <td className="px-3 py-1.5 text-[var(--sc-text)]">{p.isVerified ? "TAK" : "NIE"}</td>
                      <td className="px-3 py-1.5 text-[var(--sc-text)]">{p.comment ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div>
          <label className={labelCls}>Footer Text:</label>
          <input type="text" name="footerText" value={footerText} onChange={(e) => setFooterText(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Version Text:</label>
          <input type="text" name="versionText" value={versionText} onChange={(e) => setVersionText(e.target.value)} className={inputCls} />
        </div>
        <FollowUpLetterFiles reportId={letterId} files={fulFiles} />
        <div className="flex gap-2">
          <Button type="submit">{t("save")}</Button>
          <Button variant="secondary" type="button" onClick={() => setShowExportModal(true)}>
            Eksportuj do PDF
          </Button>
        </div>
      </form>
    </>
  );

  if (isModal) return form;

  return (
    <div className="p-6 bg-[#F7F7F5] rounded-[12px] max-w-lg mx-auto m-5">
      {form}
    </div>
  );
}

export function FollowUpLetterPage() {
  const { letterId } = useParams();
  return <FollowUpLetterContent letterId={letterId} />;
}

export function FollowUpLetterModal({
  reportId,
  open,
  onClose,
}: {
  reportId: string;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} size="md" offset="none">
      <ModalHeader title="Follow Up Letter" onClose={onClose} />
      <ModalBody>
        <FollowUpLetterContent letterId={reportId} isModal />
      </ModalBody>
    </Modal>
  );
}
